import * as React from 'react'
import styled from 'styled-components'

import * as autocomplete from '../../../behaviours/autocomplete'
import { useLoseFocus } from '../../../hooks/useLoseFocus'
import * as S from '../../../styles'

import { scrollToVisibility } from '../../../utils/scrollToVisibility'

import { SearchInput, SearchInputProps } from '../SearchInput'

export type AutocompleteProps = Omit<SearchInputProps, 'value' | 'onChange' | 'defaultValue'> & {
  className?: string
  /** the function returning results for a given query */
  fetch(query: string): Promise<autocomplete.Result[]>
  /** the initial query. Makes the component uncontrolled (incompatible with `query`)
   * @default '' */
  initialQuery?: string
  /** handler called on each query change, with the new query
   * @default () => {} */
  onQueryChange?: (query: string) => void
  /** handler called on each selection change, with the selected result
   * @default () => {} */
  onResultSelect?: (result: autocomplete.Result) => void
  /**
   * the current query. Makes the component controlled (incompatible with `initialQuery`)
   * @default '' */
  query?: string
  /** Component to render results with. Won't be rendered if no results where fetched
   * @default ({ result }) => <>{result.label}</> */
  renderResult?: React.ComponentType<{ result: autocomplete.Result }>
  /** Extra component to display under results. Won't be rendered if no results where fetched
   * @default () => {} */
  renderResultExtra?: React.ComponentType<{ close: () => void }>
  /** Component to render if no results where fetched.
   * @default () => {} */
  renderNoResult?: React.ComponentType<{ close: () => void }>
  height?: number
}

export const AutocompleteComponent = React.forwardRef<HTMLInputElement, AutocompleteProps>(
  function AutocompleteComponent(props, ref) {
    // eslint-disable-next-line no-undef
    if (process.env.NODE_ENV !== 'production') {
      React.useEffect(() => {
        if ('query' in props && 'initialQuery' in props) {
          console.error(
            new Error('<Autocomplete> is both controlled and uncontrolled, use either query or initialQuery'),
          )
        }
      }, [])
    }

    if ('query' in props) {
      return <ControlledAutocomplete {...props} ref={ref} />
    } else {
      return <UncontrolledAutocomplete {...props} ref={ref} />
    }
  },
)

export const Autocomplete = styled(AutocompleteComponent)<AutocompleteProps>``
Autocomplete.displayName = 'Autocomplete'

const UncontrolledAutocomplete = React.forwardRef<HTMLInputElement, AutocompleteProps>(
  function UncontrolledAutocomplete(
    {
      className,
      renderResult,
      fetch,
      onResultSelect = () => {},
      initialQuery = '',
      onQueryChange = () => {},
      renderNoResult,
      height,
      translations,
      renderResultExtra,
      ...inputProps
    },
    inputRef,
  ) {
    const [query, setQuery] = React.useState(initialQuery)

    function handleChange(query: string): void {
      onQueryChange && onQueryChange(query)
      setQuery(query)
    }

    function handleResultSelect(result: autocomplete.Result): void {
      onResultSelect && onResultSelect(result)
      setQuery(result.label)
    }

    return (
      <ControlledAutocomplete
        ref={inputRef}
        translations={translations}
        className={className}
        renderResult={renderResult}
        fetch={fetch}
        onResultSelect={handleResultSelect}
        query={query}
        onQueryChange={handleChange}
        height={height}
        renderNoResult={renderNoResult}
        renderResultExtra={renderResultExtra}
        {...inputProps}
      />
    )
  },
)

let currentId = 0
const ids = new WeakMap<any, number>()

const ControlledAutocomplete = React.forwardRef<HTMLInputElement, AutocompleteProps>(function ControlledAutocomplete(
  {
    className,
    renderResult: Result = ({ result }) => <>{result.label}</>,
    fetch,
    onResultSelect = () => {},
    query = '',
    onQueryChange = () => {},
    height,
    translations,
    renderNoResult: NoResult = () => <></>,
    renderResultExtra: ResultExtra = () => <></>,
    ...inputProps
  },
  inputRef,
) {
  const { isOpen, close, results, register } = autocomplete.useAutocompleteList(query, onResultSelect, fetch)

  const autocompleteBoxRef = React.useRef<HTMLDivElement>(null)
  const listBoxRef = React.useRef<HTMLUListElement>(null)

  useLoseFocus(
    autocompleteBoxRef,
    () => {
      isOpen && close()
    },
    [isOpen],
  )

  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    onQueryChange(event.target.value)
  }

  return (
    <div className={className} ref={autocompleteBoxRef}>
      <autocomplete.AutocompleteContextProvider value={register}>
        <SearchInput {...inputProps} ref={inputRef} translations={translations} value={query} onChange={handleChange} />
        <S.select.Drawer isOpen={isOpen}>
          <S.dropdown.Dropdown>
            <S.select.ScrollList height={height} ref={listBoxRef} tabIndex={-1}>
              {results.length === 0 ? (
                <NoResult close={close} />
              ) : (
                <>
                  {results.map((result, i) => {
                    if (!ids.has(result)) {
                      ids.set(result, currentId++)
                      if (currentId === Number.MAX_SAFE_INTEGER) {
                        currentId = 0
                      }
                    }
                    const id = ids.get(result)
                    return (
                      <AutocompleteResult scrollRef={listBoxRef} result={result} key={id + ':' + i}>
                        <Result result={result} />
                      </AutocompleteResult>
                    )
                  })}
                  <ResultExtra close={close} />
                </>
              )}
            </S.select.ScrollList>
          </S.dropdown.Dropdown>
        </S.select.Drawer>
      </autocomplete.AutocompleteContextProvider>
    </div>
  )
})

type AutocompleteResultProps = {
  result: autocomplete.Result
  children: React.ReactNode
  scrollRef: React.RefObject<HTMLElement>
}

function AutocompleteResult({
  result,
  scrollRef,
  children,
}: AutocompleteResultProps): React.ReactElement<AutocompleteResultProps> {
  const ref = React.useRef<HTMLLIElement>(null)
  const { select, focus, isFocused } = autocomplete.useAutocompleteItem({
    ...result,
    handleFocus: () => {
      scrollToVisibility(scrollRef?.current ?? null, ref.current)
    },
  })

  return (
    <S.autocomplete.ResultWrapper
      ref={ref}
      onMouseDown={select}
      onMouseMove={() => focus(result.value)}
      onFocus={() => focus(result.value)}
      isFocused={isFocused}
    >
      {children}
    </S.autocomplete.ResultWrapper>
  )
}
